/**
 * Background upload engine — pure module, no React.
 *
 * Drives chunked, resumable uploads against the `file-multipart-upload`
 * edge function. Persists progress in IndexedDB so uploads survive
 * navigation, network drops, logout, and browser restarts.
 */

import { supabase } from "@/integrations/supabase/client";
import { uploadQueue, type QueuedUpload, type UploadStatus } from "./queue";
import { logUploadAudit } from "./audit";
import { uploadToast } from "./toastBatcher";
import { hashBlobInWorker } from "./hashClient";
import { NotAuthenticatedError } from "./errors";

const CHUNK_SIZE_DEFAULT = 5 * 1024 * 1024; // 5 MB — R2 multipart minimum (except last part)
const CHUNK_SIZE_LARGE = 10 * 1024 * 1024; // 10 MB for files > 500 MB
const LARGE_FILE_THRESHOLD = 500 * 1024 * 1024;
const MAX_PARALLEL_FILES_DEFAULT = 2;
const MAX_PARALLEL_PARTS_DEFAULT = 3;
// P1: tightened from 6 → 4. With exponential backoff capped at 30s a single bad
// part previously kept an upload "auto-retrying" for ~18 min before failing.
// 4 attempts surfaces failures to the user in ~1.5 min so they can retry manually.
const MAX_ATTEMPTS = 4;
const BACKOFF_BASE_MS = 1000;
const BACKOFF_MAX_MS = 30000;
// Files larger than this skip SHA-256 hashing to avoid loading the entire file
// into memory at once. Hashing for files ≤ this threshold runs in a Web Worker
// (see hashClient.ts) so the UI stays responsive.
const SHA256_MAX_BYTES = 50 * 1024 * 1024; // 50 MB
// P1: debounce window to coalesce the 6 different `runQueue` triggers
// (online/offline/focus/visibility/SW message/connection/battery).
const RUN_QUEUE_DEBOUNCE_MS = 250;

// R2 requires every multipart part except the last one to be ≥ 5 MiB. If a
// future change lowers CHUNK_SIZE_DEFAULT below this, multipart `complete`
// will silently fail.
if (CHUNK_SIZE_DEFAULT < 5 * 1024 * 1024) {
  console.error("[upload-engine] CHUNK_SIZE_DEFAULT below R2 minimum (5 MiB) — uploads will fail");
}

/**
 * Connection-aware concurrency. Reads navigator.connection + battery to pick
 * gentler limits on slow networks, save-data, or low battery devices.
 */
function getAdaptiveConcurrency(): { files: number; parts: number } {
  if (typeof navigator === "undefined") {
    return { files: MAX_PARALLEL_FILES_DEFAULT, parts: MAX_PARALLEL_PARTS_DEFAULT };
  }
  const conn = (navigator as any).connection;
  let files = MAX_PARALLEL_FILES_DEFAULT;
  let parts = MAX_PARALLEL_PARTS_DEFAULT;
  if (conn) {
    if (conn.saveData) {
      files = 1; parts = 1;
    } else {
      switch (conn.effectiveType) {
        case "slow-2g":
        case "2g":
          files = 1; parts = 1; break;
        case "3g":
          files = 1; parts = 2; break;
        case "4g":
        default:
          files = MAX_PARALLEL_FILES_DEFAULT; parts = MAX_PARALLEL_PARTS_DEFAULT;
      }
    }
  }
  // Battery-aware: low battery + unplugged → halve
  const battery = (window as any).__hibaneBattery;
  if (battery && !battery.charging && typeof battery.level === "number" && battery.level < 0.2) {
    files = Math.max(1, Math.floor(files / 2));
    parts = Math.max(1, Math.floor(parts / 2));
  }
  return { files, parts };
}

async function hashFileSha256(file: File): Promise<string | undefined> {
  try {
    if (file.size > SHA256_MAX_BYTES) return undefined;
    if (!crypto?.subtle) return undefined;
    // P2: hashing now runs in a Web Worker so even small files don't stall
    // the main thread (see hashClient.ts).
    return await hashBlobInWorker(file);
  } catch (e) {
    console.warn("[upload-engine] sha256 hash failed:", e);
    return undefined;
  }
}

export interface UploadProgress {
  localUploadId: string;
  fileName: string;
  fileSize: number;
  bytesUploaded: number;
  percent: number;
  status: UploadStatus;
  error?: string;
  folderId: string | null;
  speedBps?: number;
  etaSec?: number;
  // P3: surfaces auto-retry attempts so the tray can show "Retrying 2/4…"
  attemptCount?: number;
  maxAttempts?: number;
}

export const UPLOAD_MAX_ATTEMPTS = 4;

type Listener = (progress: Map<string, UploadProgress>) => void;

class UploadEngine {
  private currentUserId: string | null = null;
  private inFlight = new Set<string>();
  private listeners = new Set<Listener>();
  private progress = new Map<string, UploadProgress>();
  private chunkBytesInFlight = new Map<string, number>(); // localId -> bytes for current parts
  // Per-localId map of partNumber -> in-flight bytes for smooth progress
  private partInFlightBytes = new Map<string, Map<number, number>>();
  // P1: cached count of parts already committed in IDB per localId. Avoids an
  // IDB read on every xhr.upload.onprogress tick. Refreshed only when a part
  // completes or after hydration.
  private committedPartCount = new Map<string, number>();
  private speedSamples = new Map<string, { ts: number; bytes: number }>();
  private aborted = new Set<string>();
  private paused = false;
  private completedCallbacks = new Set<(localId: string, folderId: string | null) => void>();
  // P1: debounce handle for runQueue.
  private runQueueTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * P1: coalesce the 6 runQueue triggers (online/offline/focus/visibility/SW
   * message/connection change/battery change) into a single debounced call so
   * a flapping network doesn't cause requeue churn.
   */
  private scheduleRunQueue() {
    if (this.runQueueTimer) return;
    this.runQueueTimer = setTimeout(() => {
      this.runQueueTimer = null;
      if (this.currentUserId) this.runQueue().catch(console.error);
    }, RUN_QUEUE_DEBOUNCE_MS);
  }

  constructor() {
    if (typeof window !== "undefined") {
      window.addEventListener("online", () => this.scheduleRunQueue());
      window.addEventListener("offline", () => {
        // Mark all in-flight as paused (they'll retry on `online`).
        if (this.inFlight.size > 0) uploadToast.pausedOffline();
        for (const id of this.inFlight) {
          uploadQueue.updateUpload(id, { status: "paused", error: "Network offline" });
          this.updateProgress(id, { status: "paused", error: "Network offline" });
        }
      });
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") {
          this.scheduleRunQueue();
        } else if (document.visibilityState === "hidden" && this.inFlight.size > 0) {
          if ("serviceWorker" in navigator) {
            navigator.serviceWorker.ready
              .then((reg) => reg.active?.postMessage({ type: "REGISTER_UPLOAD_SYNC" }))
              .catch(() => {});
          }
        }
      });
      window.addEventListener("focus", () => this.scheduleRunQueue());
      if ("serviceWorker" in navigator) {
        navigator.serviceWorker.addEventListener("message", (e: MessageEvent) => {
          if (e.data?.type === "HIBANE_UPLOAD_RESUME") this.scheduleRunQueue();
        });
      }
      // Phase C: react to network changes for adaptive concurrency
      const conn = (navigator as any).connection;
      if (conn?.addEventListener) {
        conn.addEventListener("change", () => this.scheduleRunQueue());
      }
      // Phase C: cache battery handle for adaptive concurrency
      const navAny = navigator as any;
      if (typeof navAny.getBattery === "function") {
        navAny.getBattery().then((b: any) => {
          (window as any).__hibaneBattery = b;
          b.addEventListener?.("levelchange", () => this.scheduleRunQueue());
          b.addEventListener?.("chargingchange", () => this.scheduleRunQueue());
        }).catch(() => {});
      }
    }
  }

  setCurrentUser(userId: string | null) {
    this.currentUserId = userId;
    if (!userId) {
      // signed out — we don't abort R2 multiparts, just pause the engine.
      this.paused = true;
      this.inFlight.clear();
      this.progress.clear();
      this.partInFlightBytes.clear();
      this.speedSamples.clear();
      this.chunkBytesInFlight.clear();
      this.aborted.clear();
      this.notify();
    } else {
      this.paused = false;
      // Phase D: reconcile orphans (uploading rows from a prior session that died
      // mid-flight) and prune old terminal records before showing anything.
      Promise.all([
        uploadQueue.pruneOrphans().catch((e) => console.warn("[upload-engine] pruneOrphans failed:", e)),
        uploadQueue.pruneTerminal().catch((e) => console.warn("[upload-engine] pruneTerminal failed:", e)),
      ]).then(() => {
        this.hydrateProgress(userId).then(() => {
          this.runQueue().catch(console.error);
        });
      });
    }
  }

  onProgress(fn: Listener): () => void {
    this.listeners.add(fn);
    fn(this.progress);
    return () => { this.listeners.delete(fn); };
  }

  onCompleted(fn: (localId: string, folderId: string | null) => void): () => void {
    this.completedCallbacks.add(fn);
    return () => { this.completedCallbacks.delete(fn); };
  }

  getProgress(): Map<string, UploadProgress> {
    return this.progress;
  }

  private notify() {
    const snapshot = new Map(this.progress);
    for (const l of this.listeners) l(snapshot);
  }

  private updateProgress(localId: string, patch: Partial<UploadProgress>) {
    const existing = this.progress.get(localId);
    if (!existing && !patch.fileName) return;
    const merged: UploadProgress = {
      ...(existing as UploadProgress),
      ...patch,
      localUploadId: localId,
    };
    this.progress.set(localId, merged);
    this.notify();
  }

  private async hydrateProgress(userId: string) {
    const all = await uploadQueue.listForUser(userId);
    this.progress.clear();
    const failedCutoff = Date.now() - 24 * 60 * 60 * 1000;
    for (const u of all) {
      if (u.status === "completed") continue;
      // Skip stale failed rows — they should have been pruned, but guard anyway
      // so they never reappear as "in progress" after a refresh.
      if (u.status === "failed") {
        const ts = u.lastAttempt ?? u.createdAt;
        if (ts < failedCutoff) continue;
      }
      const parts = await uploadQueue.getParts(u.localUploadId);
      // P1: seed cached committed part count for this localId.
      this.committedPartCount.set(u.localUploadId, parts.length);
      const bytesUploaded = parts.length * u.chunkSize;
      const cappedBytes = Math.min(bytesUploaded, u.fileSize);
      this.progress.set(u.localUploadId, {
        localUploadId: u.localUploadId,
        fileName: u.fileName,
        fileSize: u.fileSize,
        bytesUploaded: cappedBytes,
        percent: u.fileSize ? (cappedBytes / u.fileSize) * 100 : 0,
        // `uploading` from a prior session = orphan → show as paused. pruneOrphans()
        // will have already promoted long-stale ones to failed.
        status: u.status === "uploading" ? "paused" : u.status,
        error: u.error,
        folderId: u.folderId,
        attemptCount: u.attemptCount,
        maxAttempts: MAX_ATTEMPTS,
      });
    }
    this.notify();
  }

  async enqueue(file: File, folderId: string | null, userId: string): Promise<string> {
    const localUploadId = crypto.randomUUID();
    const chunkSize = file.size > LARGE_FILE_THRESHOLD ? CHUNK_SIZE_LARGE : CHUNK_SIZE_DEFAULT;
    const totalChunks = Math.max(1, Math.ceil(file.size / chunkSize));
    // Phase C: hash file contents for duplicate detection (best-effort).
    // We do NOT await hashing here — it blocks the main thread for large files.
    // Hash runs in the background and is patched onto the queued item if it
    // completes before init fires.
    const item: QueuedUpload = {
      localUploadId,
      userId,
      file,
      fileName: file.name,
      fileSize: file.size,
      mimeType: file.type || "application/octet-stream",
      folderId,
      chunkSize,
      totalChunks,
      status: "queued",
      createdAt: Date.now(),
      attemptCount: 0,
      sha256: undefined,
    };
    await uploadQueue.putUpload(item);
    this.updateProgress(localUploadId, {
      fileName: file.name,
      fileSize: file.size,
      bytesUploaded: 0,
      percent: 0,
      status: "queued",
      folderId,
    });
    logUploadAudit({ userId, upload: item, event: "queued", data: { totalChunks, chunkSize, hasSha256: false } });
    this.runQueue().catch(console.error);

    // Background hash for small files only — never blocks the upload start.
    if (file.size <= SHA256_MAX_BYTES) {
      hashFileSha256(file)
        .then((sha256) => {
          if (sha256) {
            uploadQueue.updateUpload(localUploadId, { sha256 }).catch(() => {});
          }
        })
        .catch(() => {});
    }

    return localUploadId;
  }

  async pause(localUploadId: string) {
    this.aborted.add(localUploadId);
    const item = await uploadQueue.updateUpload(localUploadId, { status: "paused" });
    this.updateProgress(localUploadId, { status: "paused" });
    if (item) logUploadAudit({ userId: item.userId, upload: item, event: "paused" });
  }

  async resume(localUploadId: string) {
    this.aborted.delete(localUploadId);
    const item = await uploadQueue.updateUpload(localUploadId, { status: "queued", error: undefined, attemptCount: 0 });
    this.updateProgress(localUploadId, { status: "queued", error: undefined });
    if (item) logUploadAudit({ userId: item.userId, upload: item, event: "resumed" });
    this.runQueue().catch(console.error);
  }

  async cancel(localUploadId: string) {
    this.aborted.add(localUploadId);
    const u = await uploadQueue.getUpload(localUploadId);
    if (u?.sessionId) {
      try {
        await this.invokeFn("abort", { sessionId: u.sessionId });
      } catch (e) {
        console.warn("[upload-engine] abort failed:", e);
      }
    }
    if (u) logUploadAudit({ userId: u.userId, upload: u, event: "cancelled" });
    await uploadQueue.deleteUpload(localUploadId);
    this.progress.delete(localUploadId);
    this.partInFlightBytes.delete(localUploadId);
    this.speedSamples.delete(localUploadId);
    this.chunkBytesInFlight.delete(localUploadId);
    this.committedPartCount.delete(localUploadId);
    this.aborted.delete(localUploadId);
    this.notify();
  }

  /**
   * Removes terminal entries (completed AND failed) from the tray. For failed
   * uploads we also abort the orphan R2 multipart so storage doesn't accumulate
   * abandoned sessions until the 7-day cleanup cron runs.
   */
  async clearCompleted() {
    for (const [id, p] of this.progress) {
      if (p.status === "completed" || p.status === "failed") {
        if (p.status === "failed") {
          const u = await uploadQueue.getUpload(id);
          if (u?.sessionId) {
            try {
              await this.invokeFn("abort", { sessionId: u.sessionId });
            } catch (e) {
              console.warn("[upload-engine] discard abort failed:", e);
            }
          }
        }
        await uploadQueue.deleteUpload(id);
        this.progress.delete(id);
        this.partInFlightBytes.delete(id);
        this.speedSamples.delete(id);
        this.chunkBytesInFlight.delete(id);
        this.committedPartCount.delete(id);
      }
    }
    this.notify();
  }

  async runQueue(): Promise<void> {
    if (this.paused || !this.currentUserId) return;
    if (typeof navigator !== "undefined" && navigator.onLine === false) return;

    const all = await uploadQueue.listResumableForUser(this.currentUserId);
    // Skip those already in flight or aborted in this session
    const candidates = all
      .filter((u) => !this.inFlight.has(u.localUploadId) && !this.aborted.has(u.localUploadId))
      .filter((u) => u.status !== "completed" && u.status !== "aborted")
      .sort((a, b) => a.createdAt - b.createdAt);

    const { files: maxFiles } = getAdaptiveConcurrency();
    while (this.inFlight.size < maxFiles && candidates.length > 0) {
      const next = candidates.shift()!;
      this.inFlight.add(next.localUploadId);
      this.uploadOne(next.localUploadId)
        .catch((e) => console.error("[upload-engine] uploadOne failed:", e))
        .finally(() => {
          this.inFlight.delete(next.localUploadId);
          this.scheduleRunQueue();
        });
    }
  }

  private async ensureSession(): Promise<string | null> {
    const { data } = await supabase.auth.getSession();
    if (!data.session) return null;
    // Token refresh is automatic via supabase-js; if expiring soon, force refresh.
    const exp = data.session.expires_at ? data.session.expires_at * 1000 : 0;
    if (exp && exp - Date.now() < 60_000) {
      const { data: refreshed } = await supabase.auth.refreshSession();
      return refreshed.session?.access_token || null;
    }
    return data.session.access_token;
  }

  private async invokeFn(action: string, body: Record<string, unknown>): Promise<any> {
    const token = await this.ensureSession();
    if (!token) throw new Error("AUTH_EXPIRED");
    const { data, error } = await supabase.functions.invoke("file-multipart-upload", {
      body: { action, ...body },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    if (error) throw error;
    if (data?.error) throw new Error(data.error);
    return data;
  }

  private async uploadOne(localId: string): Promise<void> {
    let item = await uploadQueue.getUpload(localId);
    if (!item) return;

    await uploadQueue.updateUpload(localId, { status: "uploading", error: undefined });
    this.updateProgress(localId, { status: "uploading", error: undefined });
    logUploadAudit({ userId: item.userId, upload: item, event: "started" });

    // 1. Init multipart if not yet
    if (!item.sessionId || !item.r2UploadId || !item.r2FileKey) {
      try {
        const init = await this.invokeFn("init", {
          fileName: item.fileName,
          fileSize: item.fileSize,
          contentType: item.mimeType,
          folderId: item.folderId,
          totalChunks: item.totalChunks,
          sha256: item.sha256,
        });
        // Server detected an existing identical file (by sha256 hash) → short-circuit.
        if (init?.duplicate && init?.existing) {
          const updated = await uploadQueue.updateUpload(localId, {
            status: "completed",
            duplicateOfFileId: init.existing.id,
            completedAt: Date.now(),
          });
          this.updateProgress(localId, {
            status: "completed",
            bytesUploaded: item.fileSize,
            percent: 100,
            error: undefined,
          });
          this.partInFlightBytes.delete(localId);
          this.speedSamples.delete(localId);
          this.chunkBytesInFlight.delete(localId);
          this.committedPartCount.delete(localId);
          logUploadAudit({
            userId: item.userId,
            upload: updated ?? item,
            event: "duplicate_detected",
            data: { existingFileId: init.existing.id },
          });
          uploadToast.duplicate(item.fileName);
          for (const cb of this.completedCallbacks) cb(localId, item.folderId);
          return;
        }
        item = (await uploadQueue.updateUpload(localId, {
          sessionId: init.sessionId,
          r2UploadId: init.uploadId,
          r2FileKey: init.fileKey,
        }))!;
        logUploadAudit({ userId: item.userId, upload: item, event: "init", data: { totalChunks: item.totalChunks } });
      } catch (e) {
        return this.handleError(localId, e);
      }
    }

    // 2. Determine remaining parts
    const uploadedPartNums = await uploadQueue.getPartNumbers(localId);
    const allParts: number[] = [];
    for (let i = 1; i <= item.totalChunks; i++) {
      if (!uploadedPartNums.has(i)) allParts.push(i);
    }

    // Update bytesUploaded baseline from already-completed parts
    const baseUploaded = Math.min(uploadedPartNums.size * item.chunkSize, item.fileSize);
    this.updateProgress(localId, {
      bytesUploaded: baseUploaded,
      percent: item.fileSize ? (baseUploaded / item.fileSize) * 100 : 0,
    });

    // 3. Upload parts with bounded concurrency
    let cursor = 0;
    const errors: unknown[] = [];

    const worker = async () => {
      while (cursor < allParts.length) {
        if (this.aborted.has(localId)) return;
        if (typeof navigator !== "undefined" && !navigator.onLine) {
          throw new Error("OFFLINE");
        }
        const partNumber = allParts[cursor++];
        try {
          await this.uploadPart(item!, partNumber);
        } catch (e) {
          errors.push(e);
          throw e;
        }
      }
    };

    const workers: Promise<void>[] = [];
    const { parts: maxParts } = getAdaptiveConcurrency();
    const concurrency = Math.min(maxParts, allParts.length);
    for (let i = 0; i < concurrency; i++) workers.push(worker());

    try {
      await Promise.all(workers);
    } catch (e) {
      return this.handleError(localId, errors[0] ?? e);
    }

    // 4. Complete (skip if user paused/cancelled mid-flight after parts settled)
    if (this.aborted.has(localId)) {
      return;
    }
    try {
      const parts = await uploadQueue.getParts(localId);
      const partsPayload = parts
        .sort((a, b) => a.partNumber - b.partNumber)
        .map((p) => ({ PartNumber: p.partNumber, ETag: p.etag }));

      try {
        await this.invokeFn("complete", {
          sessionId: item.sessionId,
          parts: partsPayload,
          sha256: item.sha256,
        });
      } catch (e) {
        // Idempotency: if R2 reports the multipart is already complete (e.g. a
        // retried `complete` after a network glitch), treat as success.
        const msg = e instanceof Error ? e.message.toLowerCase() : String(e).toLowerCase();
        const alreadyDone =
          msg.includes("nosuchupload") ||
          msg.includes("no such upload") ||
          msg.includes("already") && msg.includes("complete");
        if (!alreadyDone) throw e;
        console.info("[upload-engine] complete short-circuited (already finalised):", item.fileName);
      }

      const completed = await uploadQueue.updateUpload(localId, {
        status: "completed",
        completedAt: Date.now(),
      });
      this.updateProgress(localId, {
        status: "completed",
        bytesUploaded: item.fileSize,
        percent: 100,
      });
      this.partInFlightBytes.delete(localId);
      this.speedSamples.delete(localId);
      this.chunkBytesInFlight.delete(localId);
      logUploadAudit({
        userId: item.userId,
        upload: completed ?? item,
        event: "completed",
        data: { totalChunks: item.totalChunks, fileSize: item.fileSize },
      });
      uploadToast.uploaded(item.fileName);
      const folderId = item.folderId;
      for (const cb of this.completedCallbacks) cb(localId, folderId);
    } catch (e) {
      return this.handleError(localId, e);
    }
  }

  private async uploadPart(item: QueuedUpload, partNumber: number): Promise<void> {
    let attempt = 0;
    let presignedUrl: string | null = null;
    while (attempt < MAX_ATTEMPTS) {
      if (this.aborted.has(item.localUploadId)) return;
      try {
        if (!presignedUrl) {
          const signed = await this.invokeFn("sign-part", {
            sessionId: item.sessionId,
            partNumber,
          });
          presignedUrl = signed.presignedUrl;
        }

        const start = (partNumber - 1) * item.chunkSize;
        const end = Math.min(start + item.chunkSize, item.fileSize);
        const blob = item.file.slice(start, end);

        const etag = await this.putWithProgress(item, partNumber, presignedUrl!, blob);

        await uploadQueue.putPart({
          localUploadId: item.localUploadId,
          partNumber,
          etag,
          uploadedAt: Date.now(),
        });

        // P1: bump cache instead of re-reading IDB.
        const prevCount = this.committedPartCount.get(item.localUploadId) ?? 0;
        this.committedPartCount.set(item.localUploadId, prevCount + 1);
        const uploaded = Math.min((prevCount + 1) * item.chunkSize, item.fileSize);
        this.partInFlightBytes.get(item.localUploadId)?.delete(partNumber);
        this.recomputeProgress(item, uploaded);
        return;
      } catch (e) {
        // P1: presigned URL likely expired — re-sign instead of replaying it.
        const msg = e instanceof Error ? e.message : String(e);
        if (msg.startsWith("HTTP_403") || msg.startsWith("HTTP_400")) {
          presignedUrl = null;
        }
        attempt++;
        if (attempt >= MAX_ATTEMPTS) throw e;
        const delay = Math.min(BACKOFF_BASE_MS * Math.pow(2, attempt - 1), BACKOFF_MAX_MS);
        await new Promise((r) => setTimeout(r, delay));
      }
    }
  }

  /**
   * P1: Reads the cached `committedPartCount` instead of fetching parts from
   * IndexedDB on every progress tick. The cache is bumped only when a part
   * actually completes (see uploadPart), so progress remains accurate.
   *
   * Throws an Error whose message starts with `HTTP_403` on 403 responses so
   * the caller can recognise expired presigned URLs and re-sign immediately
   * instead of consuming a retry attempt with the same stale URL.
   */
  private putWithProgress(item: QueuedUpload, partNumber: number, url: string, blob: Blob): Promise<string> {
    const localId = item.localUploadId;
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", url);
      xhr.upload.onprogress = (e) => {
        if (!e.lengthComputable) return;
        let perPart = this.partInFlightBytes.get(localId);
        if (!perPart) {
          perPart = new Map();
          this.partInFlightBytes.set(localId, perPart);
        }
        perPart.set(partNumber, e.loaded);
        // Use cached committed count — no IDB read per tick.
        const committedParts = this.committedPartCount.get(localId) ?? 0;
        const completed = Math.min(committedParts * item.chunkSize, item.fileSize);
        let inFlight = 0;
        for (const v of perPart.values()) inFlight += v;
        const uploaded = Math.min(completed + inFlight, item.fileSize);
        this.recomputeProgress(item, uploaded);
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          const etag = xhr.getResponseHeader("ETag") || xhr.getResponseHeader("etag") || "";
          if (!etag) {
            reject(new Error("Missing ETag header from R2"));
            return;
          }
          resolve(etag.replace(/"/g, ""));
        } else {
          this.partInFlightBytes.get(localId)?.delete(partNumber);
          reject(new Error(`HTTP_${xhr.status}: Part upload failed (status ${xhr.status})`));
        }
      };
      xhr.onerror = () => {
        this.partInFlightBytes.get(localId)?.delete(partNumber);
        reject(new Error("Network error during part upload"));
      };
      xhr.onabort = () => {
        this.partInFlightBytes.get(localId)?.delete(partNumber);
        reject(new Error("Part upload aborted"));
      };
      xhr.send(blob);
    });
  }

  private recomputeProgress(item: QueuedUpload, uploadedBytes: number) {
    const sample = this.speedSamples.get(item.localUploadId);
    let speedBps: number | undefined;
    const now = Date.now();
    if (sample) {
      const dt = (now - sample.ts) / 1000;
      const db = uploadedBytes - sample.bytes;
      if (dt > 0 && db > 0) speedBps = db / dt;
    }
    // Keep speed sample stable enough for ETA: only refresh sample when ≥1s elapsed
    if (!sample || now - sample.ts >= 1000) {
      this.speedSamples.set(item.localUploadId, { ts: now, bytes: uploadedBytes });
    }
    const remaining = Math.max(0, item.fileSize - uploadedBytes);
    const etaSec = speedBps && speedBps > 0 ? remaining / speedBps : undefined;
    this.updateProgress(item.localUploadId, {
      bytesUploaded: uploadedBytes,
      percent: item.fileSize ? (uploadedBytes / item.fileSize) * 100 : 0,
      speedBps,
      etaSec,
      status: "uploading",
    });
  }

  private async handleError(localId: string, e: unknown) {
    const msg = e instanceof Error ? e.message : String(e);
    const item = await uploadQueue.getUpload(localId);
    if (!item) return;

    console.warn("[upload-engine] error on", item.fileName, "→", msg);

    if (msg === "AUTH_EXPIRED") {
      await uploadQueue.updateUpload(localId, { status: "paused", error: "Waiting for sign-in" });
      this.updateProgress(localId, { status: "paused", error: "Waiting for sign-in" });
      return;
    }
    if (msg === "OFFLINE") {
      await uploadQueue.updateUpload(localId, { status: "paused", error: "Network offline" });
      this.updateProgress(localId, { status: "paused", error: "Network offline" });
      return;
    }

    const nextAttempt = (item.attemptCount || 0) + 1;
    if (nextAttempt >= MAX_ATTEMPTS) {
      const failed = await uploadQueue.updateUpload(localId, {
        status: "failed",
        error: msg,
        attemptCount: nextAttempt,
        lastAttempt: Date.now(),
      });
      this.updateProgress(localId, { status: "failed", error: msg, attemptCount: nextAttempt, maxAttempts: MAX_ATTEMPTS });
      uploadToast.failed(item.fileName, msg);
      logUploadAudit({
        userId: item.userId,
        upload: failed ?? item,
        event: "failed",
        errorMessage: msg,
        data: { attemptCount: nextAttempt },
      });
    } else {
      await uploadQueue.updateUpload(localId, {
        status: "paused",
        error: msg,
        attemptCount: nextAttempt,
        lastAttempt: Date.now(),
      });
      this.updateProgress(localId, { status: "paused", error: msg, attemptCount: nextAttempt, maxAttempts: MAX_ATTEMPTS });
      // Auto-retry after backoff
      const delay = Math.min(BACKOFF_BASE_MS * Math.pow(2, nextAttempt - 1), BACKOFF_MAX_MS);
      console.info(`[upload-engine] retry ${nextAttempt}/${MAX_ATTEMPTS} in ${delay}ms for ${item.fileName}`);
      setTimeout(() => {
        if (!this.aborted.has(localId) && this.currentUserId === item.userId) {
          uploadQueue.updateUpload(localId, { status: "queued" }).then(() => {
            this.runQueue().catch(console.error);
          });
        }
      }, delay);
    }
  }
}

export const uploadEngine = new UploadEngine();
